import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import RunStepList from "./RunStepList";
import MeetingTimeInputModal from "./MeetingTimeInputModal";
import { fetchWorkflowRuns, resumeWorkflowRun } from "../services/workflow.service";
import type { WorkflowRun } from "./workflow.types";

interface RunHistoryPanelProps {
  open: boolean;
  workflowId: string | null;
  workflowName?: string;
  onClose: () => void;
}

const RUN_STATUS_CHIP: Record<string, { label: string; color: "success" | "error" | "default" | "info" | "warning" }> = {
  running: { label: "Running", color: "info" },
  waiting_input: { label: "Needs input", color: "warning" },
  completed: { label: "Completed", color: "success" },
  failed: { label: "Failed", color: "error" },
  stopped: { label: "Stopped", color: "default" },
};

function formatWhen(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function stepSummary(run: WorkflowRun): string {
  const failed = run.steps.filter((s) => s.status === "failed").length;
  const done = run.steps.filter((s) => s.status === "done").length;
  if (failed > 0) return `${done} done · ${failed} failed`;
  return `${done} step${done === 1 ? "" : "s"} completed`;
}

export default function RunHistoryPanel({ open, workflowId, workflowName, onClose }: RunHistoryPanelProps) {
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [resumingRunId, setResumingRunId] = useState<string | null>(null);
  const [isResuming, setIsResuming] = useState(false);

  const load = () => {
    if (!workflowId) return;
    setLoading(true);
    fetchWorkflowRuns(workflowId)
      .then((res) => setRuns(res?.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workflowId]);

  const resumingRun = runs.find((r) => r._id === resumingRunId) || null;

  const handleResume = async (fields: { date: string; startTime: string }) => {
    if (!resumingRunId) return;
    setIsResuming(true);
    try {
      await resumeWorkflowRun(resumingRunId, fields);
      setResumingRunId(null);
      load();
    } finally {
      setIsResuming(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 420, p: 2.5, height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Run history
          </Typography>
          <IconButton size="small" onClick={load} aria-label="Refresh" disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 1.5 }} />

        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {loading && runs.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          ) : runs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No runs yet — use Run or Simulate to test this workflow.
            </Typography>
          ) : (
            runs.map((run) => {
              const statusMeta = RUN_STATUS_CHIP[run.status] || { label: run.status, color: "default" as const };
              return (
                <Accordion
                  key={run._id}
                  expanded={expandedId === run._id}
                  onChange={(_, isExpanded) => setExpandedId(isExpanded ? run._id : null)}
                  disableGutters
                  sx={{
                    "&:before": { display: "none" },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    mb: 1,
                    "&.Mui-expanded": { mb: 1 },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip size="small" label={statusMeta.label} color={statusMeta.color} sx={{ height: 20 }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatWhen(run.completedAt || run.startedAt)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {run.status === "waiting_input" && run.pendingInput?.label
                          ? `Waiting on: ${run.pendingInput.label}`
                          : stepSummary(run)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {run.status === "waiting_input" && (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mb: 1.5 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setResumingRunId(run._id);
                        }}
                      >
                        Provide meeting time
                      </Button>
                    )}
                    <RunStepList run={run} />
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      </Box>

      {resumingRun?.pendingInput && (
        <MeetingTimeInputModal
          open
          workflowName={workflowName || "This workflow"}
          stepLabel={resumingRun.pendingInput.label || "this step"}
          suggested={resumingRun.pendingInput.suggested}
          isSubmitting={isResuming}
          onCancel={() => setResumingRunId(null)}
          onConfirm={handleResume}
        />
      )}
    </Drawer>
  );
}
